import type { GeoJsonObject } from '@/shared/api/types'

/** Each ring is a flat list of `[lon, lat]` pairs — GeoJSON order, not lat/lon. */
export type Ring = [number, number][]

export interface Bounds {
  minLon: number
  minLat: number
  maxLon: number
  maxLat: number
}

/**
 * Pulls every polygon ring out of a Feature, FeatureCollection, GeometryCollection
 * or bare geometry. Points and lines are ignored — this is for area geometry.
 */
export function extractRings(geo: GeoJsonObject | null | undefined): Ring[] {
  if (!geo) return []
  const out: Ring[] = []

  const walkCoords = (type: string, coords: unknown) => {
    if (!Array.isArray(coords)) return
    if (type === 'Polygon') {
      for (const ring of coords as unknown[]) {
        if (Array.isArray(ring)) out.push(ring as Ring)
      }
    } else if (type === 'MultiPolygon') {
      for (const poly of coords as unknown[]) {
        if (!Array.isArray(poly)) continue
        for (const ring of poly as unknown[]) {
          if (Array.isArray(ring)) out.push(ring as Ring)
        }
      }
    }
  }

  const walk = (node: unknown) => {
    if (!node || typeof node !== 'object') return
    const n = node as GeoJsonObject
    if (n.type === 'FeatureCollection' && Array.isArray(n.features)) {
      for (const f of n.features) walk(f)
      return
    }
    if (n.type === 'Feature' && n.geometry) { walk(n.geometry); return }
    if (n.type === 'GeometryCollection' && Array.isArray(n.geometries)) {
      for (const g of n.geometries as unknown[]) walk(g)
      return
    }
    if (typeof n.type === 'string' && n.coordinates !== undefined) {
      walkCoords(n.type, n.coordinates)
    }
  }

  walk(geo)
  // Guard against a pathological payload locking up the browser. A ring of fewer
  // than three positions cannot enclose anything.
  return out.filter((r) => r.length > 2).slice(0, 400)
}

/** Bounding box over a set of rings, or null when there is nothing numeric in them. */
export function ringBounds(rings: Ring[]): Bounds | null {
  let minLon = Infinity, maxLon = -Infinity, minLat = Infinity, maxLat = -Infinity
  for (const ring of rings) {
    for (const p of ring) {
      const [lon, lat] = p
      if (typeof lon !== 'number' || typeof lat !== 'number') continue
      if (lon < minLon) minLon = lon
      if (lon > maxLon) maxLon = lon
      if (lat < minLat) minLat = lat
      if (lat > maxLat) maxLat = lat
    }
  }
  if (!Number.isFinite(minLon) || !Number.isFinite(minLat)) return null
  return { minLon, minLat, maxLon, maxLat }
}

export function countPoints(rings: Ring[]): number {
  return rings.reduce((n, r) => n + r.length, 0)
}

/** Kenya's bounding box — the same one the backend's import validator uses. */
export const KE_BOUNDS = { minLat: -4.7, maxLat: 5.5, minLon: 33.9, maxLon: 41.9 }

export function outsideKenya(lat: number | '' | null, lon: number | '' | null): boolean {
  if (lat === '' || lon === '' || lat === null || lon === null) return false
  return lat < KE_BOUNDS.minLat || lat > KE_BOUNDS.maxLat
      || lon < KE_BOUNDS.minLon || lon > KE_BOUNDS.maxLon
}

/** `-1.286389` → `1°17'11"S`. For a read-only display beside the raw decimals. */
export function formatDms(value: number, axis: 'lat' | 'lon'): string {
  const hemisphere = axis === 'lat' ? (value >= 0 ? 'N' : 'S') : (value >= 0 ? 'E' : 'W')
  const abs = Math.abs(value)
  const deg = Math.floor(abs)
  const minFloat = (abs - deg) * 60
  const min = Math.floor(minFloat)
  const sec = Math.round((minFloat - min) * 60)
  return `${deg}°${String(min).padStart(2, '0')}'${String(sec).padStart(2, '0')}"${hemisphere}`
}
